import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { resolveStatus, useBoardStatuses } from './projectStatuses';

const FILTER_OPERATORS = {
  text: [
    { value: 'contains', label: 'contém' },
    { value: 'not_contains', label: 'não contém' },
    { value: 'equals', label: 'é igual a' },
    { value: 'not_equals', label: 'não é igual a' },
    { value: 'starts_with', label: 'começa com' },
    { value: 'ends_with', label: 'termina com' },
    { value: 'is_empty', label: 'está vazio' },
    { value: 'is_not_empty', label: 'não está vazio' },
  ],
  category: [
    { value: 'equals', label: 'é igual a' },
    { value: 'not_equals', label: 'não é igual a' },
    { value: 'contains', label: 'contém' },
    { value: 'not_contains', label: 'não contém' },
    { value: 'is_empty', label: 'está vazio' },
    { value: 'is_not_empty', label: 'não está vazio' },
  ],
  number: [
    { value: 'equals', label: 'é igual a' },
    { value: 'not_equals', label: 'não é igual a' },
    { value: 'greater_than', label: 'é maior que' },
    { value: 'less_than', label: 'é menor que' },
    { value: 'is_empty', label: 'está vazio' },
    { value: 'is_not_empty', label: 'não está vazio' },
  ],
  date: [
    { value: 'equals', label: 'é na data' },
    { value: 'greater_than', label: 'é depois de' },
    { value: 'less_than', label: 'é antes de' },
    { value: 'is_empty', label: 'está vazio' },
    { value: 'is_not_empty', label: 'não está vazio' },
  ],
};

const MATCH_MODE_OPTIONS = [
  { value: 'all', label: 'Todas as regras', description: 'O item precisa atender todas as condições.' },
  { value: 'any', label: 'Qualquer regra', description: 'O item aparece se atender pelo menos uma condição.' },
];

function createRule(column = '', operator = 'contains', value = '') {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    column,
    operator,
    value,
  };
}

function normalizeString(value) {
  return String(value ?? '').trim().toLowerCase();
}

function parseDurationToMinutes(rawValue) {
  if (rawValue === null || rawValue === undefined || rawValue === '') return null;
  if (typeof rawValue === 'number') return rawValue <= 24 ? rawValue * 60 : rawValue;

  const rawText = String(rawValue).trim().toLowerCase();
  if (!rawText) return null;

  if (/^\d{1,2}:\d{2}$/.test(rawText)) {
    const [hours, minutes] = rawText.split(':');
    return Number(hours) * 60 + Number(minutes);
  }

  const normalized = rawText.replace(',', '.');
  const matches = [...normalized.matchAll(/(\d+(?:\.\d+)?)\s*(h|hr|hrs|hora|horas|m|min|mins|minuto|minutos)/g)];
  if (matches.length) {
    return matches.reduce((total, [, amount, unit]) => {
      const numericAmount = Number(amount);
      return total + (unit.startsWith('h') || unit.startsWith('hora') ? numericAmount * 60 : numericAmount);
    }, 0);
  }

  const numericValue = Number(normalized);
  if (Number.isFinite(numericValue)) {
    return numericValue <= 24 ? numericValue * 60 : numericValue;
  }

  return null;
}

function parseDateValue(rawValue) {
  if (!rawValue) return null;
  const parsed = new Date(`${String(rawValue).slice(0, 10)}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDateLabel(rawValue) {
  const parsed = parseDateValue(rawValue);
  return parsed ? parsed.toLocaleDateString('pt-BR') : 'Sem data';
}

function formatMinutesLabel(minutes) {
  const safeMinutes = Math.max(0, Number(minutes) || 0);
  const hours = Math.floor(safeMinutes / 60);
  const remainder = safeMinutes % 60;
  if (!hours) return `${remainder}m`;
  if (!remainder) return `${hours}h`;
  return `${hours}h ${String(remainder).padStart(2, '0')}m`;
}

function resolvePersons(value) {
  const resolved = resolvePersonTokens(value);
  if (resolved.length) return resolved.join(', ');
  return value || '';
}

function resolvePersonTokens(value) {
  if (Array.isArray(value)) {
    return value
      .map(person => {
        if (!person) return null;
        if (typeof person === 'string') return person;
        return person.name || person.email || person.id || null;
      })
      .filter(Boolean);
  }
  if (!value) return [];
  if (typeof value === 'string') return [value];
  if (typeof value === 'object') {
    const personValue = value.name || value.email || value.id;
    return personValue ? [personValue] : [];
  }
  return [String(value)];
}

// Mesmas opções padrão oferecidas ao alterar status/prioridade na tabela,
// para o filtro não listar só o que já existe nas tarefas.
const DEFAULT_CATEGORY_OPTIONS = {
  priority: ['alta', 'media', 'baixa', 'critica', 'nenhuma'],
};

const CATEGORY_LABELS = {
  priority: { alta: 'Alta', media: 'Média', baixa: 'Baixa', critica: 'Crítica', nenhuma: 'Nenhuma' },
};

function getOptionLabel(column, option) {
  if (column?.id === 'status') return resolveStatus(option, column.statusConfig).label;
  return CATEGORY_LABELS[column?.id]?.[option] || option;
}

function getColumnOptions(items, column) {
  if (!column) return [];

  const values = items
    .map(item => extractItemValue(item, column))
    .filter(value => value !== '' && value !== null && value !== undefined);

  if (column.type === 'date') {
    return [...new Set(values.map(formatDateLabel))];
  }

  if (column.type === 'number') {
    return [];
  }

  const flatValues = values.flatMap(value => Array.isArray(value) ? value : [value]);
  const defaults = column.id === 'status'
    ? (column.statusConfig || []).map(status => status.key)
    : (DEFAULT_CATEGORY_OPTIONS[column.id] || []);
  return [...new Set([...defaults, ...flatValues.map(value => String(value))])]
    .sort((left, right) => left.localeCompare(right, 'pt-BR', { numeric: true }));
}

function extractItemValue(item, column) {
  const columnValues = item.column_values || {};

  switch (column.id) {
    case 'name':
      return item.name || '';
    case 'group':
      return column.groupMap?.get(item.group_id) || '';
    case 'status': {
      const rawStatus = columnValues.status || columnValues.Status || '';
      // Apelidos antigos viram a chave do status configurado no quadro.
      return rawStatus ? resolveStatus(rawStatus, column.statusConfig).key : '';
    }
    case 'priority':
      return columnValues.priority || columnValues.Priority || '';
    case 'assignee':
      return [
        ...resolvePersonTokens(columnValues.persons),
        ...resolvePersonTokens(columnValues.person),
        ...resolvePersonTokens(item.assignees),
      ];
    case 'due_date':
      return columnValues.due_date || columnValues['Due Date'] || columnValues.date || '';
    case 'estimated_time':
      return parseDurationToMinutes(columnValues.estimated_time);
    case 'difficulty':
      return columnValues.difficulty || '';
    case 'created_at':
      return item.created_at || '';
    case 'updated_at':
      return item.updated_at || '';
    default: {
      const rawValue = columnValues[column.id];
      if (column.type === 'date') return rawValue || '';
      if (column.type === 'number') {
        const minutes = parseDurationToMinutes(rawValue);
        if (minutes !== null) return minutes;
        const numericValue = Number(rawValue);
        return Number.isFinite(numericValue) ? numericValue : null;
      }
      if (column.type === 'category') {
        const peopleValues = resolvePersonTokens(rawValue);
        return peopleValues.length ? peopleValues : rawValue || '';
      }
      return rawValue || '';
    }
  }
}

function ruleNeedsValue(operator) {
  return !['is_empty', 'is_not_empty'].includes(operator);
}

function matchRule(itemValue, operator, filterValue, type) {
  if (Array.isArray(itemValue)) {
    if (operator === 'is_empty') return itemValue.length === 0;
    if (operator === 'is_not_empty') return itemValue.length > 0;

    const normalizedValues = itemValue.map(value => normalizeString(value)).filter(Boolean);
    const normalizedFilter = normalizeString(filterValue);

    if (operator === 'equals') return normalizedValues.some(value => value === normalizedFilter);
    if (operator === 'not_equals') return normalizedValues.every(value => value !== normalizedFilter);
    if (operator === 'contains') return normalizedValues.some(value => value.includes(normalizedFilter));
    if (operator === 'not_contains') return normalizedValues.every(value => !value.includes(normalizedFilter));
    if (operator === 'starts_with') return normalizedValues.some(value => value.startsWith(normalizedFilter));
    if (operator === 'ends_with') return normalizedValues.some(value => value.endsWith(normalizedFilter));
    return false;
  }

  if (operator === 'is_empty') {
    return itemValue === '' || itemValue === null || itemValue === undefined;
  }
  if (operator === 'is_not_empty') {
    return !(itemValue === '' || itemValue === null || itemValue === undefined);
  }

  if (type === 'number') {
    const left = Number(itemValue);
    const right = Number(filterValue);
    if (!Number.isFinite(left) || !Number.isFinite(right)) return false;
    if (operator === 'equals') return left === right;
    if (operator === 'not_equals') return left !== right;
    if (operator === 'greater_than') return left > right;
    if (operator === 'less_than') return left < right;
    return false;
  }

  if (type === 'date') {
    const left = parseDateValue(itemValue);
    const right = parseDateValue(filterValue);
    if (!left || !right) return false;
    const leftTime = left.getTime();
    const rightTime = right.getTime();
    if (operator === 'equals') return leftTime === rightTime;
    if (operator === 'greater_than') return leftTime > rightTime;
    if (operator === 'less_than') return leftTime < rightTime;
    if (operator === 'not_equals') return leftTime !== rightTime;
    return false;
  }

  const left = normalizeString(itemValue);
  const right = normalizeString(filterValue);
  if (operator === 'equals') return left === right;
  if (operator === 'not_equals') return left !== right;
  if (operator === 'contains') return left.includes(right);
  if (operator === 'not_contains') return !left.includes(right);
  if (operator === 'starts_with') return left.startsWith(right);
  if (operator === 'ends_with') return left.endsWith(right);
  return false;
}

function hydrateSavedFilter(savedFilter) {
  const rawFilters = savedFilter?.filters;
  if (Array.isArray(rawFilters)) {
    const hasOr = rawFilters.some(rule => rule.logic === 'OR');
    return {
      matchMode: hasOr ? 'any' : 'all',
      rules: rawFilters.map(rule => ({
        id: rule.id || createRule().id,
        column: rule.column || '',
        operator: rule.operator || 'contains',
        value: rule.value || '',
      })),
    };
  }

  return {
    matchMode: rawFilters?.matchMode === 'any' ? 'any' : 'all',
    rules: Array.isArray(rawFilters?.rules)
      ? rawFilters.rules.map(rule => ({
          id: rule.id || createRule().id,
          column: rule.column || '',
          operator: rule.operator || 'contains',
          value: rule.value || '',
        }))
      : [createRule()],
  };
}

function buildAppliedRule(rule, columns) {
  const column = columns.find(entry => entry.id === rule.column);
  const operator = (FILTER_OPERATORS[column?.type || 'text'] || FILTER_OPERATORS.text)
    .find(entry => entry.value === rule.operator);

  let displayValue = rule.value;
  if (column?.type === 'date' && rule.value) {
    displayValue = formatDateLabel(rule.value);
  } else if (column?.type === 'number') {
    const parsedMinutes = parseDurationToMinutes(rule.value);
    displayValue = parsedMinutes !== null ? formatMinutesLabel(parsedMinutes) : rule.value;
  } else if (Array.isArray(rule.value)) {
    displayValue = rule.value.join(', ');
  }

  return {
    id: rule.id,
    column: rule.column,
    columnTitle: column?.title || rule.column,
    operator: rule.operator,
    operatorLabel: operator?.label || rule.operator,
    value: rule.value,
    displayValue: displayValue || '',
    type: column?.type || 'text',
  };
}

export default function ProjectFilters({
  board,
  api,
  items,
  insights = null,
  customColumns = [],
  groups = [],
  onFiltersApply,
  isOpen,
  onClose,
}) {
  const [rules, setRules] = useState([createRule()]);
  const [matchMode, setMatchMode] = useState('all');
  const [savedFilters, setSavedFilters] = useState([]);
  const [saveName, setSaveName] = useState('');
  const [loading, setLoading] = useState(false);
  const { statuses: statusConfig } = useBoardStatuses(api, board?.id);

  const columns = useMemo(() => {
    const groupMap = new Map((groups || []).map(group => [group.id, group.title]));
    const baseColumns = [
      { id: 'name', title: 'Nome', type: 'text' },
      { id: 'group', title: 'Grupo', type: 'category', groupMap },
      { id: 'status', title: 'Status', type: 'category', statusConfig },
      { id: 'priority', title: 'Prioridade', type: 'category' },
      { id: 'assignee', title: 'Responsáveis', type: 'category' },
      { id: 'due_date', title: 'Prazo', type: 'date' },
      { id: 'estimated_time', title: 'Estimativa', type: 'number' },
      { id: 'difficulty', title: 'Dificuldade', type: 'category' },
      { id: 'created_at', title: 'Criado em', type: 'date' },
      { id: 'updated_at', title: 'Atualizado em', type: 'date' },
    ];

    const custom = (customColumns || []).map(column => {
      const normalizedType = String(column.type || '').toLowerCase();
      const type = ['numbers', 'number', 'progress', 'progresso', 'rating'].includes(normalizedType)
        ? 'number'
        : ['date', 'data'].includes(normalizedType)
          ? 'date'
          : ['person', 'people', 'status', 'dropdown', 'checkbox', 'check', 'select', 'priority', 'time_tracking'].includes(normalizedType)
            ? 'category'
            : 'text';
      return {
        id: column.id,
        title: column.title,
        type,
      };
    });

    return [...baseColumns, ...custom];
  }, [customColumns, groups, statusConfig]);

  const filterCandidates = useMemo(() => {
    if (insights?.groups?.length) {
      return insights.groups.flatMap(group => (group.items || []).flatMap(parentItem => {
        const parentCandidate = {
          ...parentItem,
          column_values: parentItem.column_values || {},
          __candidateType: 'task',
          __parentId: parentItem.id,
          __groupTitle: group.name,
        };
        const subtasks = (parentItem.rollup?.subtasks || []).map(subtask => ({
          ...subtask,
          __candidateType: 'subtask',
          __parentId: parentItem.id,
          group_id: parentItem.group_id,
          __groupTitle: group.name,
        }));
        return [parentCandidate, ...subtasks];
      }));
    }
    return (items || []).map(item => ({
      ...item,
      __candidateType: 'task',
      __parentId: item.id,
    }));
  }, [insights, items]);

  const validRules = useMemo(
    () => rules.filter(rule => rule.column && rule.operator && (!ruleNeedsValue(rule.operator) || String(rule.value || '').trim() !== '')),
    [rules]
  );

  const filterResult = useMemo(() => {
    if (!validRules.length) {
      return {
        previewItems: items || [],
        matchedParentIds: new Set((items || []).map(item => item.id)),
        matchedSubtaskIds: new Set(),
        matchedSubtasksCount: 0,
      };
    }

    const matchedCandidates = filterCandidates.filter(item => {
      const evaluations = validRules.map(rule => {
        const column = columns.find(entry => entry.id === rule.column);
        if (!column) return true;
        const itemValue = extractItemValue(item, column);
        const preparedValue = column.type === 'number' ? parseDurationToMinutes(rule.value) ?? Number(rule.value) : rule.value;
        return matchRule(itemValue, rule.operator, preparedValue, column.type);
      });
      return matchMode === 'any' ? evaluations.some(Boolean) : evaluations.every(Boolean);
    });

    const matchedParentIds = new Set();
    const matchedSubtaskIds = new Set();
    matchedCandidates.forEach(candidate => {
      matchedParentIds.add(candidate.__candidateType === 'subtask' ? candidate.__parentId : candidate.id);
      if (candidate.__candidateType === 'subtask') matchedSubtaskIds.add(candidate.id);
    });

    return {
      previewItems: (items || []).filter(item => matchedParentIds.has(item.id)),
      matchedParentIds,
      matchedSubtaskIds,
      matchedSubtasksCount: matchedSubtaskIds.size,
    };
  }, [columns, filterCandidates, items, matchMode, validRules]);

  const previewItems = filterResult.previewItems;

  useEffect(() => {
    if (!isOpen || !board) return;
    const fetchSavedFilters = async () => {
      try {
        const response = await api.get(`/projects/boards/${board.id}/filters`);
        setSavedFilters(response.data || []);
      } catch (error) {
        console.error('Error fetching saved filters:', error);
      }
    };
    fetchSavedFilters();
  }, [api, board, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setSaveName('');
  }, [isOpen]);

  const updateRule = (ruleId, patch) => {
    setRules(currentRules => currentRules.map(rule => {
      if (rule.id !== ruleId) return rule;
      const nextRule = { ...rule, ...patch };
      if (patch.column) {
        const nextColumn = columns.find(column => column.id === patch.column);
        const nextOperators = FILTER_OPERATORS[nextColumn?.type || 'text'] || FILTER_OPERATORS.text;
        if (!nextOperators.some(operator => operator.value === nextRule.operator)) {
          nextRule.operator = nextOperators[0].value;
          nextRule.value = '';
        }
      }
      if (patch.operator && !ruleNeedsValue(patch.operator)) {
        nextRule.value = '';
      }
      return nextRule;
    }));
  };

  const addRule = () => {
    setRules(currentRules => [...currentRules, createRule()]);
  };

  const removeRule = (ruleId) => {
    setRules(currentRules => {
      if (currentRules.length === 1) return [createRule()];
      return currentRules.filter(rule => rule.id !== ruleId);
    });
  };

  const clearAll = () => {
    setRules([createRule()]);
    setMatchMode('all');
    onFiltersApply({
      items: items || [],
      matchedParentIds: [],
      matchedSubtaskIds: [],
      hasRules: false,
      matchMode: 'all',
      appliedRules: [],
    });
    toast.success('Filtros removidos. A lista voltou ao estado completo.');
  };

  const applyFilters = () => {
    onFiltersApply({
      items: previewItems,
      matchedParentIds: Array.from(filterResult.matchedParentIds),
      matchedSubtaskIds: Array.from(filterResult.matchedSubtaskIds),
      hasRules: validRules.length > 0,
      matchMode,
      appliedRules: validRules.map(rule => buildAppliedRule(rule, columns)),
    });
    toast.success(`${previewItems.length} tarefa(s) visíveis, com ${filterResult.matchedSubtasksCount} subtarefa(s) correspondendo às regras.`);
  };

  const saveCurrentFilters = async () => {
    if (!saveName.trim()) {
      toast.error('Defina um nome para salvar este conjunto.');
      return;
    }
    if (!validRules.length) {
      toast.error('Adicione pelo menos uma regra válida.');
      return;
    }

    try {
      setLoading(true);
      await api.post(`/projects/boards/${board.id}/filters`, {
        name: saveName.trim(),
        filters: {
          version: 2,
          matchMode,
          rules: validRules,
        },
      });
      const response = await api.get(`/projects/boards/${board.id}/filters`);
      setSavedFilters(response.data || []);
      setSaveName('');
      toast.success('Conjunto de filtros salvo.');
    } catch (error) {
      console.error('Error saving filters:', error);
      toast.error('Erro ao salvar filtros.');
    } finally {
      setLoading(false);
    }
  };

  const loadSavedFilter = (savedFilter) => {
    const hydrated = hydrateSavedFilter(savedFilter);
    setMatchMode(hydrated.matchMode);
    setRules(hydrated.rules.length ? hydrated.rules : [createRule()]);
    toast.success(`Conjunto "${savedFilter.name}" carregado.`);
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl overflow-y-auto lg:overflow-hidden border-slate-200 p-0">
        <DialogHeader className="border-b border-slate-200 bg-white px-6 py-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <DialogTitle className="text-xl font-semibold text-slate-900">Filtros avançados</DialogTitle>
              <p className="mt-1 text-sm text-slate-500">
                Monte regras previsíveis para refinar a visão do board sem depender da lógica antiga de filtros mistos.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="rounded-full border-blue-200 bg-blue-50 px-3 py-1 text-blue-700">
                {validRules.length} regra(s) válida(s)
              </Badge>
              <Badge variant="outline" className="rounded-full border-slate-200 bg-white px-3 py-1 text-slate-700">
                {previewItems.length} de {(items || []).length} tarefas · {filterResult.matchedSubtasksCount} subtarefa(s)
              </Badge>
            </div>
          </div>
        </DialogHeader>

        <div className="grid lg:max-h-[82vh] lg:min-h-[620px] lg:overflow-hidden lg:grid-cols-[1.45fr_0.85fr]">
          <section className="flex min-h-0 flex-col border-r border-slate-200 bg-white">
            <div className="border-b border-slate-200 px-6 py-5">
              <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Combinação</Label>
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    {MATCH_MODE_OPTIONS.map(option => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setMatchMode(option.value)}
                        className={`rounded-2xl border px-4 py-3 text-left transition-colors ${
                          matchMode === option.value
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-slate-200 bg-white hover:bg-slate-50'
                        }`}
                      >
                        <p className="font-semibold text-slate-900">{option.label}</p>
                        <p className="mt-1 text-xs text-slate-500">{option.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 lg:justify-end">
                  <Button type="button" variant="outline" onClick={clearAll}>
                    Limpar
                  </Button>
                  <Button type="button" variant="outline" onClick={addRule}>
                    + Nova regra
                  </Button>
                </div>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
              <div className="space-y-4">
                {rules.map((rule, index) => {
                  const selectedColumn = columns.find(column => column.id === rule.column);
                  const operators = FILTER_OPERATORS[selectedColumn?.type || 'text'] || FILTER_OPERATORS.text;
                  const valueOptions = getColumnOptions(filterCandidates, selectedColumn);
                  const useValueSelect = selectedColumn?.type === 'category' && valueOptions.length > 0;

                  return (
                    <article key={rule.id} className="rounded-3xl border border-slate-200 bg-slate-50/70 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                            {index + 1}
                          </span>
                          <div>
                            <p className="text-sm font-semibold text-slate-900">Regra {index + 1}</p>
                            <p className="text-xs text-slate-500">
                              {index === 0 ? 'Primeira condição da busca.' : `Esta regra entra em ${matchMode === 'all' ? 'todas as condições' : 'qualquer condição'}.`}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeRule(rule.id)}
                          className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-500 hover:border-red-200 hover:text-red-600"
                        >
                          Remover
                        </button>
                      </div>

                      <div className="mt-4 grid gap-3 xl:grid-cols-[1.1fr_0.9fr_1fr]">
                        <div>
                          <Label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Campo</Label>
                          <Select value={rule.column || '__empty__'} onValueChange={value => updateRule(rule.id, { column: value === '__empty__' ? '' : value })}>
                            <SelectTrigger className="mt-2 h-11 rounded-xl border-slate-200 bg-white">
                              <SelectValue placeholder="Escolha um campo" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__empty__">Selecione</SelectItem>
                              {columns.map(column => (
                                <SelectItem key={column.id} value={column.id}>
                                  {column.title}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Operador</Label>
                          <Select value={rule.operator} onValueChange={value => updateRule(rule.id, { operator: value })}>
                            <SelectTrigger className="mt-2 h-11 rounded-xl border-slate-200 bg-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {operators.map(operator => (
                                <SelectItem key={operator.value} value={operator.value}>
                                  {operator.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Valor</Label>
                          {ruleNeedsValue(rule.operator) ? (
                            useValueSelect ? (
                              <Select value={String(rule.value || '__empty__')} onValueChange={value => updateRule(rule.id, { value: value === '__empty__' ? '' : value })}>
                                <SelectTrigger className="mt-2 h-11 rounded-xl border-slate-200 bg-white">
                                  <SelectValue placeholder="Escolha um valor" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__empty__">Selecione</SelectItem>
                                  {valueOptions.map(option => (
                                    <SelectItem key={option} value={option}>
                                      {getOptionLabel(selectedColumn, option)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Input
                                type={selectedColumn?.type === 'date' ? 'date' : selectedColumn?.type === 'number' ? 'text' : 'text'}
                                value={rule.value}
                                onChange={event => updateRule(rule.id, { value: event.target.value })}
                                placeholder={
                                  selectedColumn?.type === 'number'
                                    ? 'Ex.: 4h, 90m ou 120'
                                    : selectedColumn?.type === 'date'
                                      ? ''
                                      : 'Valor para comparação'
                                }
                                className="mt-2 h-11 rounded-xl border-slate-200 bg-white"
                              />
                            )
                          ) : (
                            <div className="mt-2 flex h-11 items-center rounded-xl border border-dashed border-slate-200 bg-white px-3 text-sm text-slate-500">
                              Este operador não exige valor.
                            </div>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>

            <div className="border-t border-slate-200 bg-white px-6 py-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="text-sm text-slate-500">
                  {validRules.length
                    ? `Preview atualizado: ${previewItems.length} item(ns) atendem às regras atuais.`
                    : 'Nenhuma regra válida ainda. Sem regra, a visualização permanece completa.'}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" onClick={onClose}>
                    Fechar
                  </Button>
                  <Button type="button" onClick={applyFilters}>
                    Aplicar filtros
                  </Button>
                </div>
              </div>
            </div>
          </section>

          <aside className="flex min-h-0 flex-col bg-slate-50/70">
            <div className="border-b border-slate-200 px-6 py-5">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Salvar conjunto</p>
              <div className="mt-3 space-y-3">
                <Input
                  value={saveName}
                  onChange={event => setSaveName(event.target.value)}
                  placeholder="Ex.: Atrasadas da semana"
                  className="h-11 rounded-xl border-slate-200 bg-white"
                />
                <Button type="button" className="w-full" onClick={saveCurrentFilters} disabled={loading}>
                  {loading ? 'Salvando...' : 'Salvar conjunto atual'}
                </Button>
              </div>
            </div>

            <div className="border-b border-slate-200 px-6 py-5">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Resumo atual</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                  <p className="text-xs text-slate-400">Combinação</p>
                  <p className="mt-1 font-semibold text-slate-900">
                    {matchMode === 'all' ? 'Todas as regras' : 'Qualquer regra'}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                  <p className="text-xs text-slate-400">Preview</p>
                  <p className="mt-1 font-semibold text-slate-900">
                    {previewItems.length} / {(items || []).length} itens
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                  <p className="text-xs text-slate-400">Subtarefas atendidas</p>
                  <p className="mt-1 font-semibold text-slate-900">
                    {filterResult.matchedSubtasksCount}
                  </p>
                </div>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Salvos</p>
                <Badge variant="outline" className="border-slate-200 bg-white text-slate-600">
                  {savedFilters.length}
                </Badge>
              </div>

              <div className="mt-4 space-y-3">
                {!savedFilters.length && (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-6 text-sm text-slate-500">
                    Nenhum conjunto salvo para este board.
                  </div>
                )}

                {savedFilters.map(savedFilter => {
                  const hydrated = hydrateSavedFilter(savedFilter);
                  return (
                    <button
                      key={savedFilter.id}
                      type="button"
                      onClick={() => loadSavedFilter(savedFilter)}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-4 text-left transition-colors hover:border-blue-300 hover:bg-blue-50/50"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-slate-900">{savedFilter.name}</p>
                          <p className="mt-1 text-xs text-slate-500">
                            {hydrated.matchMode === 'all' ? 'Todas as regras' : 'Qualquer regra'} · {hydrated.rules.length} regra(s)
                          </p>
                        </div>
                        <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] font-semibold text-slate-600">
                          Carregar
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>
        </div>
      </DialogContent>
    </Dialog>
  );
}
